package com.royceecoleman.bill.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.royceecoleman.bill.auth.AuthenticatedSession
import com.royceecoleman.bill.auth.canAccessAdminDashboard
import com.royceecoleman.bill.auth.fetchAuthenticatedSession
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Main
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Composable
fun CardPage(
    title: String,
    supportingText: String? = null,
    content: @Composable () -> Unit = {},
) {
    Main(
        attrs = {
            style {
                property("min-height", "100vh")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("padding", "1.5rem")
                property("background", "#0f172a")
                property(
                    "font-family",
                    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                )
            }
        },
    ) {
        Div(
            attrs = {
                style {
                    property("width", "min(42rem, 100%)")
                    property("padding", "2rem")
                    property("border-radius", "1rem")
                    property("background", "rgba(255, 255, 255, 0.97)")
                    property("box-shadow", "0 24px 60px rgba(15, 23, 42, 0.35)")
                }
            },
        ) {
            H1(
                attrs = {
                    style {
                        property("margin", "0")
                        property("font-size", "2rem")
                    }
                },
            ) {
                Text(title)
            }

            if (supportingText != null) {
                P(
                    attrs = {
                        style {
                            property("margin", "0.9rem 0 0")
                            property("color", "#475569")
                            property("line-height", "1.6")
                        }
                    },
                ) {
                    Text(supportingText)
                }
            }

            content()
        }
    }
}

@Composable
fun StatusCardPage(
    title: String,
    message: String,
) {
    CardPage(
        title = title,
        supportingText = message,
    )
}

sealed interface AdminPageAccessState {
    data object CheckingSession : AdminPageAccessState

    data object RedirectingToLogin : AdminPageAccessState

    data object RedirectingToMap : AdminPageAccessState

    data object SessionCheckFailed : AdminPageAccessState

    data class Ready(
        val session: AuthenticatedSession,
    ) : AdminPageAccessState
}

sealed interface AuthenticatedPageAccessState {
    data object CheckingSession : AuthenticatedPageAccessState

    data object RedirectingToLogin : AuthenticatedPageAccessState

    data object SessionCheckFailed : AuthenticatedPageAccessState

    data class Ready(
        val session: AuthenticatedSession,
    ) : AuthenticatedPageAccessState
}

suspend fun resolveAuthenticatedPageAccessState(): AuthenticatedPageAccessState =
    try {
        val authenticatedSession = fetchAuthenticatedSession()

        if (authenticatedSession == null) {
            AuthenticatedPageAccessState.RedirectingToLogin
        } else {
            AuthenticatedPageAccessState.Ready(authenticatedSession)
        }
    } catch (_: Throwable) {
        AuthenticatedPageAccessState.SessionCheckFailed
    }

suspend fun resolveAdminPageAccessState(): AdminPageAccessState =
    try {
        val authenticatedSession = fetchAuthenticatedSession()

        when {
            authenticatedSession == null -> AdminPageAccessState.RedirectingToLogin
            authenticatedSession.role.canAccessAdminDashboard() -> AdminPageAccessState.Ready(authenticatedSession)
            else -> AdminPageAccessState.RedirectingToMap
        }
    } catch (_: Throwable) {
        AdminPageAccessState.SessionCheckFailed
    }

@Composable
fun GuardedAuthenticatedPage(
    checkingMessage: String,
    redirectingToLoginMessage: String,
    content: @Composable (AuthenticatedSession) -> Unit,
) {
    var accessState by remember { mutableStateOf<AuthenticatedPageAccessState>(AuthenticatedPageAccessState.CheckingSession) }

    LaunchedEffect(Unit) {
        accessState = resolveAuthenticatedPageAccessState()

        if (accessState == AuthenticatedPageAccessState.RedirectingToLogin) {
            window.location.href = "/login"
        }
    }

    when (val currentAccessState = accessState) {
        AuthenticatedPageAccessState.CheckingSession ->
            StatusCardPage(
                title = "Checking your session",
                message = checkingMessage,
            )

        AuthenticatedPageAccessState.RedirectingToLogin ->
            StatusCardPage(
                title = "Redirecting to login",
                message = redirectingToLoginMessage,
            )

        AuthenticatedPageAccessState.SessionCheckFailed ->
            StatusCardPage(
                title = "Unable to verify your session",
                message = "Start Bill Main and refresh the page.",
            )

        is AuthenticatedPageAccessState.Ready -> content(currentAccessState.session)
    }
}

@Composable
fun GuardedAdminPage(
    checkingMessage: String,
    redirectingToLoginMessage: String,
    redirectingToMapMessage: String,
    content: @Composable (AuthenticatedSession) -> Unit,
) {
    var accessState by remember { mutableStateOf<AdminPageAccessState>(AdminPageAccessState.CheckingSession) }

    LaunchedEffect(Unit) {
        accessState = resolveAdminPageAccessState()

        when (accessState) {
            AdminPageAccessState.RedirectingToLogin -> {
                window.location.href = "/login"
            }

            AdminPageAccessState.RedirectingToMap -> {
                window.location.href = "/"
            }

            else -> Unit
        }
    }

    when (val currentAccessState = accessState) {
        AdminPageAccessState.CheckingSession ->
            StatusCardPage(
                title = "Checking your session",
                message = checkingMessage,
            )

        AdminPageAccessState.RedirectingToLogin ->
            StatusCardPage(
                title = "Redirecting to login",
                message = redirectingToLoginMessage,
            )

        AdminPageAccessState.RedirectingToMap ->
            StatusCardPage(
                title = "Redirecting to the map",
                message = redirectingToMapMessage,
            )

        AdminPageAccessState.SessionCheckFailed ->
            StatusCardPage(
                title = "Unable to verify your session",
                message = "Start Bill Main and refresh the page.",
            )

        is AdminPageAccessState.Ready -> content(currentAccessState.session)
    }
}
